export type ProjectSaveRetryMode = 'manual' | 'automatic' | 'none';

export interface ProjectSaveRetryDecision {
    mode: ProjectSaveRetryMode;
    reasonCode: string;
}

export interface ManualProjectSaveRetryLatch {
    projectId: string | null;
    fingerprint: string;
    reasonCode: string;
    at: number;
}

type ErrorLike = {
    body?: unknown;
    cause?: unknown;
    code?: unknown;
    message?: unknown;
    name?: unknown;
    retryOwner?: unknown;
    status?: unknown;
    statusCode?: unknown;
};

const MAX_CAUSE_DEPTH = 8;

function errorChain(error: unknown): ErrorLike[] {
    const chain: ErrorLike[] = [];
    const seen = new Set<object>();
    let cursor = error;

    while (cursor && typeof cursor === 'object' && chain.length < MAX_CAUSE_DEPTH) {
        if (seen.has(cursor)) break;
        seen.add(cursor);
        const item = cursor as ErrorLike;
        chain.push(item);
        cursor = item.cause;
    }

    return chain;
}

function stringCode(value: unknown): string | null {
    return typeof value === 'string' && value.trim() ? value.trim() : null;
}

function errorCode(item: ErrorLike): string | null {
    const direct = stringCode(item.code);
    if (direct) return direct;
    if (item.body && typeof item.body === 'object') {
        return stringCode((item.body as { code?: unknown }).code);
    }
    return null;
}

export function isProjectSaveRetryBlocked(
    latch: ManualProjectSaveRetryLatch | null,
    projectId: string | null,
    fingerprint: string,
): boolean {
    return !!latch?.fingerprint
        && latch.projectId === projectId
        && latch.fingerprint === fingerprint;
}

export function classifyProjectSaveRetry(error: unknown): ProjectSaveRetryDecision {
    const chain = errorChain(error);

    for (const item of chain) {
        const name = typeof item.name === 'string' ? item.name : '';
        const message = typeof item.message === 'string' ? item.message : '';
        const code = errorCode(item);
        if (name === 'AbortError' || /aborted a request|operation was aborted|user aborted/i.test(message)) {
            return { mode: 'none', reasonCode: 'aborted' };
        }
        if (code === 'PROJECT_STALE_WRITE') {
            return { mode: 'none', reasonCode: code };
        }
    }

    for (let index = chain.length - 1; index >= 0; index -= 1) {
        const item = chain[index];
        if (!item) continue;
        const status = item.status ?? item.statusCode;
        if (status === 0 || status === 408 || status === 425 || status === 429
            || (typeof status === 'number' && status >= 500)) {
            return {
                mode: 'automatic',
                reasonCode: errorCode(item) || `http_${status}`,
            };
        }
    }

    for (let index = chain.length - 1; index >= 0; index -= 1) {
        const item = chain[index];
        if (!item) continue;
        const status = item.status ?? item.statusCode;
        if (typeof status === 'number' && status >= 400 && status < 500) {
            return {
                mode: 'manual',
                reasonCode: errorCode(item) || `http_${status}`,
            };
        }
    }

    for (const item of chain) {
        const name = typeof item.name === 'string' ? item.name : '';
        const message = typeof item.message === 'string' ? item.message : '';
        if (name === 'TimeoutError' || /failed to fetch|network error|network request|offline|timed? out|connection (?:dropped|refused|reset)/i.test(message)) {
            return { mode: 'automatic', reasonCode: name === 'TimeoutError' ? 'timeout' : 'network' };
        }
    }

    // The persistent upload queue already owns this PDF: its worker is sending
    // it, or the claim is cooling down after a failed attempt. That arrives
    // with no cause (document-persistence.js), and the next save picks up the
    // confirmed upload, so nothing here needs the operator's Retry. A latch
    // left an outage's edits unsaved after the server came back (outage lane
    // review, 2026-09-29). A queue-owned upload WITH a cause is judged by that
    // cause above and below.
    for (const item of chain) {
        if (errorCode(item) === 'DOCUMENT_DURABILITY_REQUIRED'
            && item.retryOwner === 'upload-queue'
            && item.cause == null) {
            return { mode: 'automatic', reasonCode: 'UPLOAD_QUEUE_WAITING' };
        }
    }

    for (let index = chain.length - 1; index >= 0; index -= 1) {
        const item = chain[index];
        if (!item) continue;
        const code = errorCode(item);
        if (code === 'DOCUMENT_DURABILITY_REQUIRED'
            || code === 'MEDIA_DURABILITY_REQUIRED'
            || code === 'INLINE_CUSTOMER_MEDIA_FORBIDDEN') {
            return { mode: 'manual', reasonCode: code };
        }
    }

    return { mode: 'automatic', reasonCode: 'unknown' };
}
